import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, RefreshCw, User, Key, FileText, Building, UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toastBanner";
import { logEvent } from "@/lib/events";
import { sanitizeHtml } from "@/utils/sanitize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CredentialCard({ credential }) {
  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4 animate-in fade-in duration-300 space-y-3"
      data-testid="generated-credential"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-gray-400 font-body">
            <User className="h-3 w-3" />
            Login ID
          </div>
          <p className="text-sm font-body font-semibold text-gray-900 font-mono select-all" data-testid="cred-display-login">
            {credential.login_id}
          </p>
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-gray-400 font-body">
            <Key className="h-3 w-3" />
            Password
          </div>
          <p className="text-sm font-body font-semibold text-gray-900 font-mono select-all" data-testid="cred-display-password">
            {credential.password}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-gray-100">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-gray-400 font-body">
            <Building className="h-3 w-3" />
            Department
          </div>
          <p className="text-sm font-body font-semibold text-gray-900 select-all" data-testid="cred-display-department">
            {credential.department || "—"}
          </p>
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-gray-400 font-body">
            <UserCircle className="h-3 w-3" />
            Patient Name
          </div>
          <p className="text-sm font-body font-semibold text-gray-900 select-all" data-testid="cred-display-patient-name">
            {credential.patient_name || "—"}
          </p>
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-gray-400 font-body">
            <FileText className="h-3 w-3" />
            Patient MRN
          </div>
          <p className="text-sm font-body font-semibold text-gray-900 font-mono select-all" data-testid="cred-display-mrn">
            {credential.patient_mrn}
          </p>
        </div>
      </div>
    </div>
  );
}

function RoleGenerator({ role }) {
  const [credential, setCredential] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.get(`${API}/learner/epic-practice/roles/${role.id}/generate`, { withCredentials: true });
      setCredential(data);
      logEvent("credential_generated", { role_id: role.id, role_name: role.name });
    } catch {
      toast.error("Failed to generate credential");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`role-generator-${role.id}`}>
      <Button
        onClick={generate}
        disabled={generating}
        data-testid={`generate-btn-${role.id}`}
        className="h-10 px-5 font-body font-medium text-white rounded-xl hover:opacity-90 text-sm"
        style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
      >
        {generating ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : credential ? (
          <RefreshCw className="h-4 w-4 mr-2" />
        ) : null}
        {credential ? `Generate another ${role.name} login` : `Generate a ${role.name} login and patient`}
      </Button>
      {credential && <CredentialCard credential={credential} />}
    </div>
  );
}

export default function LearnerEpicPractice() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/learner/epic-practice`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    logEvent("epic_practice_viewed");
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const instructions = data?.instructions || "";
  const roles = data?.roles || [];
  const label = (data?.label || "").trim() || "Sandbox";

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="learner-epic-practice">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">{label}</h1>

      {/* Instructions */}
      {instructions && (
        <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="epic-instructions">
          <div
            className="text-sm text-gray-600 font-body prose max-w-none prose-headings:text-gray-900 prose-a:text-blue-600 prose-a:underline prose-strong:text-gray-900 [&_a]:target-blank"
            className="ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(instructions).replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ') }}
          />
        </div>
      )}

      {/* Role generators */}
      {roles.length > 0 ? (
        <div className="space-y-6">
          {roles.map((role) => (
            <RoleGenerator key={role.id} role={role} />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <p className="text-sm text-gray-400 font-body">No practice credentials are available yet.</p>
        </div>
      )}
    </div>
  );
}
